import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DeleteHook from "../../../Hooks/DeleteHook";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import { useQuery } from "../../../Hooks/useApi";

const SuperAdminDoctorFaq = () => {
  const [refetch, setRefetch] = useState(false);
  const { data: doctorFaqResponse } = useQuery("/doctorFaq/getdoctorFaq");
  const doctorFaq = doctorFaqResponse?.data || [];
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
  });

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (formData?._id) {
        await UpdateHooks(
          `https://servercareseebd.careseebd.com/api/v1/doctorFaq/updateDoctorFaq/${formData?._id}`,
          formData
        );
        toast.success("Doctor FAQ updated successfully!");
        setFormData({ question: "", answer: "" });
        setRefetch(!refetch);
      } else {
        await PostHooks(
          `https://servercareseebd.careseebd.com/api/v1/doctorFaq/addDoctorFaq`,
          formData,
          "Doctor FAQ entry posted successfully!"
        );
        setFormData({
          question: "",
          answer: "",
        });
        setTimeout(() => {
          setRefetch(!refetch);
        }, 1000);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="max-w-6xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:question-square-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Doctor FAQ Settings</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Manage frequently asked questions targeted specifically to medical professionals</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form panel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4 h-fit">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">
            {formData?._id ? "Edit FAQ Entry" : "Add FAQ Entry"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Question</label>
              <input
                type="text"
                name="question"
                onChange={handleChange}
                value={formData.question}
                required
                placeholder="e.g. How are patient reports verified?"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Answer Text</label>
              <textarea
                name="answer"
                onChange={handleChange}
                value={formData.answer}
                required
                rows="5"
                placeholder="Write the FAQ answer details..."
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-medium"
              />
            </div>

            <div className="flex gap-2">
              {formData?._id && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ question: "", answer: "" });
                  }}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-extrabold text-[11px] transition-all hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-[11px] rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                {formData?._id ? "Update FAQ" : "Add FAQ"}
              </button>
            </div>
          </form>
        </div>

        {/* List panel */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">Active Doctor FAQ Directory</h2>
          <div className="overflow-x-auto border border-slate-100 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Question</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Answer Preview</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {doctorFaq.length > 0 ? (
                  doctorFaq.map((faq) => (
                    <tr key={faq?._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 text-xs font-bold text-slate-800 max-w-xs truncate" title={faq?.question}>
                        {faq?.question}
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-slate-500 max-w-sm truncate" title={faq?.answer}>
                        {faq?.answer}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex justify-end items-center gap-2">
                          <button
                            onClick={async () => {
                              setFormData(faq);
                              scrollToTop();
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-150 rounded-lg transition-all cursor-pointer"
                            title="Edit FAQ"
                          >
                            <Icon icon="basil:edit-outline" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              DeleteHook({
                                setRefetch,
                                refetch,
                                url: `https://servercareseebd.careseebd.com/api/v1/doctorFaq/deleteDoctorFaq/${faq?._id}`,
                              })
                            }
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-150 rounded-lg transition-all cursor-pointer"
                            title="Delete FAQ"
                          >
                            <Icon icon="material-symbols:delete-outline" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-10 text-xs text-slate-400">
                      No FAQ entries added.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDoctorFaq;
